import { RelativesList } from "@pages/Deviations/Content/Overview/Relatives/RelativesList";
import { IApiFile, TDateFormatToJSDate } from "@propely/propely-typings";
import { AriaButton } from "@src/components/ui-components/Buttons/AriaButton/AriaButton";
import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { FilePreview } from "@src/components/ui-components/FilePreview/FilePreview";
import { FilePreviewModal } from "@src/components/ui-components/FilePreview/FilePreviewModal";
import { useDownloadFile } from "@src/utils/hooks/useDownloadFile";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { GlobalFileData } from "../../../to-be-removed/LocalData/Files/GlobalFileData";
import { FileDocumentData } from "../../../to-be-removed/LocalData/General/FileDocumentData";
import { FirebaseFile } from "../../../to-be-removed/LocalData/General/FirebaseFile";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { NumberUtilities } from "../../../utils/numberUtilities";
import { Utilities } from "../../../utils/utilities";
import { FileTypeImageView } from "../../ui-components/FileTypeImageView/FileTypeImageView";
import { SectionSplitterLabel } from "../../ui-components/SectionSplitterLabel/SectionSplitterLabel";
import { Label } from "../Label/Label";
import { Popup } from "../Popup/Popup";
import { PopupContainer } from "../PopupContainer/PopupContainer";
import { Text } from "../Text/Text";
import DisplayedFileData from "./DisplayedFileData";
import { DocumentAI } from "./DocumentAI";
import styles from "./FilePopup.module.css";

const convertFile = (file: FileDocumentData | FirebaseFile | DisplayedFileData | GlobalFileData | IApiFile) => {
  return file instanceof DisplayedFileData ? file : new DisplayedFileData(file);
};

type Props = {
  id: string;
  file: FileDocumentData | FirebaseFile | DisplayedFileData | GlobalFileData | IApiFile;
  otherFiles?: (FileDocumentData | FirebaseFile | DisplayedFileData)[];
  removeDownloadButton?: boolean;
};

/**
 * @deprecated Use {@link FilePreviewModal} instead.
 */
export const FilePopup = (props: Props) => {
  const [, setLastUpdate] = useState(new Date());
  const [showOtherClassifications, setShowOtherClassifications] = useState(false);
  const [file, setFile] = useState<DisplayedFileData>(convertFile(props.file));
  const { isDownloading, download } = useDownloadFile(file, file.propertyID);

  const currentFileIndex = props.otherFiles?.findIndex((otherFile) => otherFile.id === file.id) ?? -1;
  const hasNextFile = currentFileIndex !== -1 && props.otherFiles && currentFileIndex < props.otherFiles.length - 1;
  const hasPreviousFile = currentFileIndex > 0;

  useEffect(() => {
    setFile(convertFile(props.file));
  }, [props.file]);

  useEffect(() => {
    file.loadMetadata(() => {
      setLastUpdate(new Date());
    });
  }, [file]);

  // Add event listeners
  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  });

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      Popup.remove(props.id);
    } else if (event.key === "ArrowLeft" && hasPreviousFile) {
      setFile(convertFile(props.otherFiles![currentFileIndex - 1]));
    } else if (event.key === "ArrowRight" && hasNextFile) {
      setFile(convertFile(props.otherFiles![currentFileIndex + 1]));
    }
  }

  /** @todo implement! */
  //function onDeleteButtonClicked() {}

  return (
    <PopupContainer
      id={props.id}
      centered={true}
      closeWithBackshadow={true}
      children={
        <div id={styles.container}>
          <div id={styles.titleBar}>
            <div id={styles.title}>
              {/** Needs imgContainer div as FileTypeImageView has width=100% */}
              <div className={styles.imgContainer}>
                <FileTypeImageView fileType={file.type} />
              </div>
              <Label
                data-cy="file-popup-file-name"
                text={`${file.name || t("main.notAvailable")}`}
                type="header-2"
                className={styles.header}
                underlineWidth={0}
                color="black"
              />
            </div>

            {hasPreviousFile && (
              <Button
                type="text"
                leadingIcon="chevron_left"
                onClick={() => props.otherFiles && setFile(convertFile(props.otherFiles[currentFileIndex - 1]))}
              />
            )}
            {hasNextFile && (
              <Button
                type="text"
                trailingIcon="chevron_right"
                onClick={() => props.otherFiles && setFile(convertFile(props.otherFiles[currentFileIndex + 1]))}
                marginRight={32}
              />
            )}
            <i data-cy="file-popup-close-button" onClick={() => Popup.remove(props.id)} className={`material-icons ${styles.closeButton}`}>
              clear
            </i>
          </div>

          <div id={styles.horizontalFlex}>
            <div id={styles.contentContainer} data-cy="file-popup-content-container">
              <FilePreview file={file} />
            </div>
            <div id={styles.sideBar}>
              <ul id={styles.info}>
                <li>
                  <InfoElement title={t("main.filetype")} description={`${file.type.toLowerCase() || t("main.notAvailable")}`}></InfoElement>
                </li>
                <li>
                  <InfoElement title={t("main.size")} description={`${file.size || t("main.notAvailable")}`}></InfoElement>
                </li>
                <li>
                  <InfoElement
                    title={t("main.date") + " " + t("main.uploaded").toLowerCase()}
                    description={`${file.createdBy?.date ? Utilities.formatDate(file.createdBy.date, "dd/mm/yyyy hh:MM") : t("main.notAvailable")}`}
                  />
                </li>

                {file.createdBy?.user && (
                  <li>
                    <InfoElement title={t("main.uploaded") + " " + t("main.by")} description={file.createdBy.user.name} />
                  </li>
                )}

                {file.dataFields.drawingDate && (
                  <li>
                    <InfoElement
                      title={t("documentAI.dataFields.drawingDate")}
                      description={Utilities.formatDate(TDateFormatToJSDate.safeParse(file.dataFields.drawingDate).data, "dd/mm/yyyy")}
                    />
                  </li>
                )}
                {props.file instanceof GlobalFileData && LocalData.moduleActivatedOnAnyProperties("documentAI") && (
                  <>
                    <SectionSplitterLabel text={t("documentAI.title")} marginTop={16} />
                    {props.file.contentSummary && (
                      <li>
                        <InfoElement title={t("documentAI.AISummary.title")} description={props.file.contentSummary} icon="ambient_screen" />
                      </li>
                    )}
                    {props.file.classifications && props.file.classifications.length > 0 && (
                      <li>
                        <div className={styles.classificationHeader}>
                          <Text size="md" color="secondary" text={t("documentAI.classifications.classifications")} />
                          <Button
                            size="sm"
                            type="text"
                            title={t("documentAI.classifications." + (showOtherClassifications ? "showLessButton" : "showMoreButton"))}
                            trailingIcon={showOtherClassifications ? "expand_less" : "expand_more"}
                            onClick={() => setShowOtherClassifications(!showOtherClassifications)}
                          />
                        </div>
                        <ul>
                          {props.file.classifications.map(
                            (classification, index) =>
                              (showOtherClassifications || index === 0) && (
                                <li key={classification.type} className={styles.classificationRow}>
                                  <Text
                                    size="md"
                                    isBold={index === 0}
                                    text={DocumentAI.getSystemCode(classification.type)}
                                    color={index === 0 ? "accent" : "primary"}
                                  />
                                  <Text
                                    size="sm"
                                    text={NumberUtilities.formatValueToString(classification.confidence ?? 0, "percentage")}
                                    color="secondary"
                                  />
                                </li>
                              )
                          )}
                        </ul>
                      </li>
                    )}
                    {props.file.relatives.length > 0 && (
                      <li>
                        <SectionSplitterLabel text={t("main.connections")} />
                        <RelativesList relatives={props.file.relatives} />
                      </li>
                    )}
                  </>
                )}
              </ul>
              {!props.removeDownloadButton && (
                <div id={styles.stackedButtons}>
                  <AriaButton isLoading={isDownloading} data-cy="file-popup-download-button" onPress={download} className={styles.downloadButton}>
                    {t("main.download")}
                  </AriaButton>
                </div>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

const InfoElement = ({ title, description, icon }: { title: string; description: string; icon?: string }) => {
  return (
    <div className={styles.infoElement}>
      <Text leadingIcon={icon} size="sm" color="secondary">
        {title}
      </Text>
      <Text size="md" isBold>
        {description}
      </Text>
    </div>
  );
};
